import { useMemo } from 'react'
import { Undo2, FileCheck } from 'lucide-react'
import Paper from '@/components/Paper'
import {
    DataTableCustomized,
    FilterBar,
    DataTableAction,
} from '@/components/ui/data-table-customized'
import { useConfirmation } from '@/contexts/confirmation'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    rollbackStatementService,
    runStatementService,
} from '@/modules/Service/services'
import { Service } from '@/modules/Service/types'
import { useStatementServiceList } from '@/modules/Service/hooks'

const Statement = () => {
    const { setConfirmation } = useConfirmation()

    const {
        services,
        pagination,
        stateComponent,
        isFetching,
        params,
        control,
        setFilter,
        searchInputProps,
        columns,
        filterItems,
        handleSortChange,
        handlePageChange,
        handlePageSizeChange,
        t,
    } = useStatementServiceList()

    // Both mutations refetch the statement list on success. Server errors
    // (e.g. rollback blocked by invoice status) are surfaced by useMutationWithHandler.
    const { mutateAsync: runStatement } = useMutationWithHandler(
        { mutationFn: runStatementService },
        {
            successMessage: t('run-statement-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES_STATEMENT,
        }
    )

    const { mutateAsync: rollback } = useMutationWithHandler(
        { mutationFn: rollbackStatementService },
        {
            successMessage: t('rollback-statement-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES_STATEMENT,
        }
    )

    const rowActions = useMemo<DataTableAction<Service>[]>(
        () => [
            {
                key: 'run-statement',
                label: t('run-statement'),
                icon: <FileCheck className="w-4 h-4" />,
                variant: 'info',
                onClick: (row) => {
                    setConfirmation({
                        open: true,
                        title: t('run-statement-confirm-title'),
                        description: t('run-statement-confirm-desc'),
                        onConfirm: () => runStatement(row?.service_id),
                    })
                },
            },
            {
                key: 'rollback',
                label: t('rollback-statement'),
                icon: <Undo2 className="w-4 h-4" />,
                variant: 'danger',
                // Only available once the service has an active statement snapshot
                // (i.e. it was already reconciled this cycle).
                hidden: (row) => !row?.has_active_snapshot,
                onClick: (row) => {
                    setConfirmation({
                        open: true,
                        title: t('rollback-statement-confirm-title'),
                        description: t('rollback-statement-confirm-desc'),
                        onConfirm: () => rollback(row?.service_id),
                    })
                },
            },
        ],
        [t, runStatement, rollback, setConfirmation]
    )

    return (
        <Paper>
            <FilterBar
                control={control}
                setFilter={setFilter}
                items={filterItems}
                hideSearch
            />

            {stateComponent || (
                <DataTableCustomized
                    columns={columns}
                    rows={services}
                    rowKey={(row) => String(row?.service_id)}
                    size="small"
                    resizable
                    loading={!services.length && !pagination}
                    isFetching={isFetching}
                    emptyMessage={t('no-services')}
                    actions={rowActions}
                    sorting={{
                        sortBy: params.sort_by,
                        sortOrder: params.sort_order,
                        onSortChange: handleSortChange,
                    }}
                    pagination={{
                        page: params.page || 1,
                        pageSize: params.page_size || 20,
                        total: pagination?.total || 0,
                        onPageChange: handlePageChange,
                        onPageSizeChange: handlePageSizeChange,
                        pageSizeOptions: [10, 20, 50, 100],
                    }}
                    toolbar={{
                        search: {
                            ...searchInputProps,
                            placeholder: t('search-service'),
                        },
                        settings: {
                            show: true,
                            storageKey: 'services-statement-list',
                        },
                    }}
                />
            )}
        </Paper>
    )
}

export default Statement
